import React, { useState, useRef, useEffect, useCallback } from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CopilotChatPanel from "./CopilotChatPanel";
import useCopilotAgentStatus from "./useCopilotAgentStatus";
import { createClickAwaySuppressor } from "./clickAwaySuppressor";
import axiosInstance from "services/axiosInstance";
import { GET_CONFIGURATION_VALUES } from "services/CONSTANTS";
import styles from "./CopilotChatFab.module.css";

// ASTT-7409: parallel evaluation of the Copilot Studio agent alongside the
// existing ADK/Cloud Run one (AgentChatFab). Same allowlist as
// AgentChatFab.js -- the same people evaluating the ADK agent are the ones
// this Copilot Studio comparison is for. Widen or remove once the agent is
// considered ready to compare more broadly, same rollout-gate pattern
// AgentChatFab and NlQueryFab both already use.
//
// SOURCED FROM CONFIG, NOT HARDCODED: fetched once on mount from
// appsettings' CopilotAgentAllowedUsers key via the existing
// getConfigurationValue endpoint (agenttrackersearch/getConfigurationValue)
// -- already authenticated, already used elsewhere in the app, no new
// backend code needed. Stored as ONE comma-separated string (not a JSON
// array) specifically so Azure App Service's Application Settings only
// need ONE variable to edit -- an array would otherwise need one
// Key__0/Key__1/... setting per person, which gets painful to maintain
// as the list changes. GetConfigurationValue returns a flat string as a
// one-entry dictionary keyed by the config key's own name (see
// TrackerSearchService.GetConfigurationValue's "no children" branch) --
// Object.values(raw)[0] below grabs that single value, then splits it.
// Updating the allowlist is now a config change, not a code deploy.
const CONFIG_KEY_ALLOWED_USERS = "CopilotAgentAllowedUsers";

const GREET_DELAY_MS = 900;
const GREET_DISMISSED_KEY = "astt-copilot-agent-greet-dismissed";

/**
 * Floating action button that toggles CopilotChatPanel -- same launcher
 * pattern, same icon, and same status-dot mechanics as AgentChatFab (the
 * ADK/Cloud Run agent's launcher), reused deliberately so both look like
 * they belong to the same ASTT agent family rather than two unrelated
 * widgets. Mounted alongside AgentChatFab, not replacing it -- offset via
 * CopilotChatFab.module.css's launcherWrap so the two buttons don't overlap
 * during this parallel evaluation.
 */
export default function CopilotChatFab() {
  const { isAuthenticated, user } = useOidcAuth();
  const [open, setOpen] = useState(false);
  const [showGreet, setShowGreet] = useState(false);
  const mountedRef = useRef(false);
  // ASTT-7496 (QA #10/#20): one suppressor instance for this Fab's whole
  // lifetime -- created once via a ref, not per-render.
  const clickAwaySuppressorRef = useRef(null);
  if (!clickAwaySuppressorRef.current) {
    clickAwaySuppressorRef.current = createClickAwaySuppressor();
  }
  // Passed down to CopilotChatPanel and called right before it triggers a
  // download (conversation export, per-table CSV export) -- see
  // handleClickAway below for the other half of this fix. useCallback with
  // no deps: this only ever touches the ref, so the SAME function identity
  // is safe across every render, which matters because the panel's own
  // effect that registers window.__astt_exportMdTable depends on this
  // reference not changing on every keystroke/message. Declared here,
  // BEFORE the `if (!isAllowed) return null` below -- a Hook call (even a
  // trivial one like this) must never become conditional across renders,
  // and isAllowed can flip during this component's lifetime.
  const handleBeforeDownload = useCallback(() => {
    clickAwaySuppressorRef.current?.suppressNext();
  }, []);

  // null = "haven't heard back from config yet" -- deliberately distinct
  // from an empty array ("config says nobody"), so the FAB renders nothing
  // while this is unresolved rather than flashing visible-then-hidden (or
  // worse, briefly visible to someone who turns out not to be allowed).
  // Fails CLOSED on a fetch error -- an empty array, not letting everyone
  // in -- since this gates an active evaluation rollout, not a feature
  // that's safe to default-open.
  const [allowedUsers, setAllowedUsers] = useState(null);

  useEffect(() => {
    // Don't fire this authenticated-only call before PingOne has resolved a
    // signed-in user -- e.g. while the login screen itself is showing, or
    // during the auth provider's own startup window right after app load.
    // This component mounts unconditionally in MainContainer with no
    // route/auth gating, so without this check the call fires immediately
    // on every page load including the login screen itself, gets a 401
    // (no token to send), and axiosInstance's response interceptor treats
    // EVERY 401 -- from ANY call, not just this one -- as "session
    // expired" and force-reloads via window.location.replace("/"). That
    // reload re-mounts this same component, which fires the same request
    // again, which 401s again -- an infinite loop that made the entire
    // login screen unusable for every user, not just this feature. Depend
    // on isAuthenticated so the fetch correctly retries once login
    // actually completes, rather than only ever getting one chance at
    // mount time.
    if (!isAuthenticated) {
      return undefined;
    }

    let cancelled = false;
    axiosInstance
      .get(GET_CONFIGURATION_VALUES(), { params: { ConfigurationKey: CONFIG_KEY_ALLOWED_USERS } })
      .then((response) => {
        if (cancelled) return;
        const raw = response?.data?.data;
        const rawValue = raw && typeof raw === "object" ? Object.values(raw)[0] : "";
        const list =
          typeof rawValue === "string"
            ? rawValue.split(",").map((e) => e.trim()).filter(Boolean)
            : [];
        setAllowedUsers(list.map((e) => e.toLowerCase()));
      })
      .catch(() => {
        if (!cancelled) setAllowedUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // NOT YET VERIFIED AGAINST A LIVE TOKEN: which claim PingOne's ID token
  // uses for the user's email/username. Falls back through the standard
  // OIDC claims until confirmed.
  const currentUser = (
    user?.profile?.email ??
    user?.profile?.preferred_username ??
    user?.profile?.unique_name ??
    ""
  ).toLowerCase();
  // ASTT-7496: named greetingName, not firstName -- since the PingOne
  // claim-mapping fix (separate from this codebase), given_name now holds
  // the user's COMPLETE name, not just a first name, so this variable's
  // old name no longer matched what it actually held. Purely a naming
  // correction; the fallback chain itself is unchanged.
  const greetingName =
    user?.profile?.given_name ?? user?.profile?.name?.split(" ")?.[0] ?? "there";
  const isAllowed = allowedUsers !== null && allowedUsers.includes(currentUser);

  const agentStatus = useCopilotAgentStatus(isAllowed);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isAllowed) return undefined;
    if (localStorage.getItem(GREET_DISMISSED_KEY) === "true") return undefined;

    const timer = setTimeout(() => {
      if (mountedRef.current && !open) setShowGreet(true);
    }, GREET_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowed]);

  if (!isAllowed) return null;

  const dismissGreet = () => {
    setShowGreet(false);
    localStorage.setItem(GREET_DISMISSED_KEY, "true");
  };

  const handleToggle = () => {
    if (!mountedRef.current) return;
    setOpen((prev) => !prev);
    if (!open) dismissGreet();
  };

  const handleClose = () => {
    if (!mountedRef.current) return;
    setOpen(false);
  };

  // ASTT-7496 (QA #10/#20): ONLY for ClickAwayListener's onClickAway below
  // -- a just-triggered download's transient document.body anchor click
  // fires as an "outside click" here, since that anchor sits outside this
  // component's wrapped subtree. The panel's own explicit header Close
  // button calls handleClose directly (not this), so a deliberate close
  // click is never at risk of being swallowed by a pending suppression
  // window from a moments-ago export.
  const handleClickAway = () => {
    if (clickAwaySuppressorRef.current.consumeSuppression()) return;
    handleClose();
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div className={styles.launcherWrap}>
        {open && (
          <CopilotChatPanel onClose={handleClose} agentStatus={agentStatus} onBeforeDownload={handleBeforeDownload} />
        )}

        {showGreet && !open && (
          <div className={styles.greetBubble}>
            <button className={styles.greetClose} onClick={dismissGreet} aria-label="Dismiss">
              <CloseIcon sx={{ fontSize: 12 }} />
            </button>
            Hi <b>{greetingName}</b> — I'm the ASTT Assistant. Ask me about a trainee, roster, or
            class request, equipment/Taleo/OMT status, or how a process works.
          </div>
        )}

        <button
          className={styles.fab}
          onClick={handleToggle}
          title="ASTT Assistant (Copilot Studio)"
          aria-label="Open ASTT Assistant"
        >
          <SmartToyOutlinedIcon sx={{ fontSize: 22 }} />
          <span
            className={`${styles.fabStatusDot} ${
              agentStatus === "online"
                ? styles.fabStatusOnline
                : agentStatus === "offline"
                  ? styles.fabStatusOffline
                  : styles.fabStatusChecking
            }`}
            aria-hidden="true"
          >
            {agentStatus !== "online" && <span className={styles.fabStatusPing} />}
          </span>
        </button>
      </div>
    </ClickAwayListener>
  );
}
